import { useState, useCallback } from "react";
import { Flex, Text, Button, toast } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useAppContext } from "~/contexts/appContext";
import {
  useAssignReviewerMutation,
  useResolveChangeRequestMutation,
} from "~/graphql/supergraphHooks";
import { isBillingAdmin } from "~/utils/userRoles";
import { ReviewerActionFooter } from "./ReviewerActionFooter";
import { ReviewerConclusion } from "./types";
import type { CaseStatus } from "../types";
import type { ResolveAction } from "~/graphql/supergraphTypes";

const CONCLUSION_OPTIONS = Object.entries(ReviewerConclusion).map(([label, value]) => ({
  value,
  label,
}));

const getActionForConclusion = (conclusion: ReviewerConclusion): ResolveAction => {
  switch (conclusion) {
    case ReviewerConclusion.Approved:
      return "APPROVE";
    case ReviewerConclusion.Invalid:
      return "MARK_INVALID";
    default: {
      const exhaustiveCheck: never = conclusion;
      throw new Error(`Unhandled conclusion: ${String(exhaustiveCheck)}`);
    }
  }
};

const reviewerConclusionValues: string[] = Object.values(ReviewerConclusion);
const conclusionsRequiringNote: ReviewerConclusion[] = [ReviewerConclusion.Invalid];

export type CheckerFooterProps = {
  changeRequestId: string;
  assigneeName: string;
  assigneeId: number | null;
  onConclusionSubmit: (conclusion: ReviewerConclusion) => void;
  onStatusChange: (status: CaseStatus) => void;
};

export const CheckerFooter = ({
  changeRequestId,
  assigneeName,
  assigneeId,
  onConclusionSubmit,
  onStatusChange,
}: CheckerFooterProps) => {
  const hasRealAssignee = assigneeName !== "Unassigned" && assigneeName !== "";
  const [isAssigned, setIsAssigned] = useState(hasRealAssignee);
  const [displayedAssignee, setDisplayedAssignee] = useState(assigneeName);
  const [currentAssigneeId, setCurrentAssigneeId] = useState<number | null>(assigneeId);
  const [conclusion, setConclusion] = useState<string>("");
  const [noteModalConclusion, setNoteModalConclusion] = useState<ReviewerConclusion | null>(
    null,
  );

  const { user } = useAppContext();
  const userRoles = user?.roles ?? [];
  const isAdmin = isBillingAdmin(userRoles);
  const isAssigneeOrAdmin = user?.id === currentAssigneeId || isAdmin;

  const [resolveChangeRequest, { loading: resolveLoading }] = useResolveChangeRequestMutation({
    onCompleted: (data) => {
      if (data.resolveChangeRequest?.changeRequest) {
        toast.success("Change request resolved successfully");
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const [assignReviewer, { loading: assignLoading }] = useAssignReviewerMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const isValidConclusion = (value: string): value is ReviewerConclusion =>
    reviewerConclusionValues.includes(value);

  const submitResolution = useCallback(
    async (selectedConclusion: ReviewerConclusion, reason?: string) => {
      const action = getActionForConclusion(selectedConclusion);
      const result = await resolveChangeRequest({
        variables: {
          input: {
            changeRequestId,
            action,
            decisionReason: reason ?? null,
          },
        },
      });
      const changeRequest = result.data?.resolveChangeRequest?.changeRequest;
      if (changeRequest) {
        onConclusionSubmit(selectedConclusion);
        setConclusion("");
        if (changeRequest.status) {
          onStatusChange(changeRequest.status);
        }
      }
    },
    [resolveChangeRequest, changeRequestId, onConclusionSubmit, onStatusChange],
  );

  const handleCancelAssignment = useCallback(async () => {
    const result = await assignReviewer({
      variables: { changeRequestId, reviewerId: null },
    });
    const changeRequest = result.data?.assignReviewer?.changeRequest;
    if (changeRequest) {
      toast.success("Assignment cancelled successfully");
      setIsAssigned(false);
      setCurrentAssigneeId(null);
      setConclusion("");
      setNoteModalConclusion(null);
      if (changeRequest.status) {
        onStatusChange(changeRequest.status);
      }
    }
  }, [assignReviewer, changeRequestId, onStatusChange]);

  const handleAssignToMyself = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    const result = await assignReviewer({
      variables: { changeRequestId, reviewerId: user.id },
    });
    const changeRequest = result.data?.assignReviewer?.changeRequest;
    if (changeRequest) {
      toast.success("Reviewer assigned successfully");
      const name = String(changeRequest.assigneeName || (user.fullName ?? "You"));
      setDisplayedAssignee(name);
      setCurrentAssigneeId(user.id);
      setIsAssigned(true);
      if (changeRequest.status) {
        onStatusChange(changeRequest.status);
      }
    }
  }, [assignReviewer, changeRequestId, onStatusChange, user?.id, user?.fullName]);

  const handleSubmit = useCallback(
    (selectedConclusion: ReviewerConclusion) => {
      if (conclusionsRequiringNote.includes(selectedConclusion)) {
        setNoteModalConclusion(selectedConclusion);
        return;
      }
      void submitResolution(selectedConclusion);
    },
    [submitResolution],
  );

  const handleNoteSubmit = useCallback(
    (note: string) => {
      if (noteModalConclusion) {
        void submitResolution(noteModalConclusion, note);
      }
      setNoteModalConclusion(null);
    },
    [noteModalConclusion, submitResolution],
  );

  const handleNoteModalClose = () => {
    setConclusion("");
    setNoteModalConclusion(null);
  };

  const noteModalConfig = { title: "Invalid reason", placeholder: "State invalid reason" };

  const isLoading = resolveLoading || assignLoading;

  if (!isAssigned) {
    return (
      <Flex alignItems="center" gap={Sizes.SM}>
        <Text variant="secondary-md" color={Colors.Neutral600}>
          Unassigned
        </Text>
        {isAdmin && (
          <Button
            isInline
            variant="neutral"
            onClick={handleAssignToMyself}
            disabled={isLoading}
          >
            Assign to myself
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <ReviewerActionFooter
      assigneeName={displayedAssignee}
      showCancelAssignment={isAssigneeOrAdmin}
      onCancelAssignment={handleCancelAssignment}
      showDecisionControls={isAssigneeOrAdmin}
      conclusion={conclusion}
      onConclusionChange={setConclusion}
      conclusionOptions={CONCLUSION_OPTIONS}
      selectPlaceholder="Select reviewer conclusion"
      selectTestId="conclusion-select"
      onSubmit={() => isValidConclusion(conclusion) && handleSubmit(conclusion)}
      isLoading={isLoading}
      isReasonModalOpen={noteModalConclusion !== null}
      reasonModalTitle={noteModalConfig.title}
      reasonModalPlaceholder={noteModalConfig.placeholder}
      onReasonModalClose={handleNoteModalClose}
      onReasonModalSubmit={handleNoteSubmit}
    />
  );
};
